/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AiTranslatorProfileFormView.tsx
 🧩 Description:
    Create / Edit form for AI Translator Profiles.
    Uses the standard High-Density Configuration Pattern:
    ModuleFormHeader (sibling) + p-6 pb-24 mt-20 container + FixedActionBar.

 🔁 Last Updated: 2026-05-12 by Rodrigo Cuadra (Standardized)
────────────────────────────────────────────────────────────────*/

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Languages } from 'lucide-react';

import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { Toggle } from '../../components/ui/Toggle';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { Label } from '../../components/ui/label';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { aiTranslatorProfilesApi } from './api/aiTranslatorProfilesApi';
import type { AiTranslatorProfileUpdatePayload } from './api/types';
import { getAiProfiles, type AiProfile } from '../ai-profiles/api/aiProfilesApi';

// ── Language / voice options ────────────────────────────────────────────────

const LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English (en)' },
    { value: 'es', label: 'Spanish (es)' },
    { value: 'fr', label: 'French (fr)' },
    { value: 'de', label: 'German (de)' },
    { value: 'pt', label: 'Portuguese (pt)' },
    { value: 'it', label: 'Italian (it)' },
    { value: 'ja', label: 'Japanese (ja)' },
    { value: 'zh', label: 'Chinese (zh)' },
    { value: 'ko', label: 'Korean (ko)' },
    { value: 'ar', label: 'Arabic (ar)' },
    { value: 'ru', label: 'Russian (ru)' },
    { value: 'nl', label: 'Dutch (nl)' },
    { value: 'pl', label: 'Polish (pl)' },
    { value: 'tr', label: 'Turkish (tr)' },
    { value: 'sv', label: 'Swedish (sv)' },
];

const VOICE_OPTIONS = [
    { value: 'alloy', label: 'Alloy (Neutral)' },
    { value: 'ash',   label: 'Ash (Neutral)' },
    { value: 'ballad', label: 'Ballad (Neutral)' },
    { value: 'coral', label: 'Coral (Female)' },
    { value: 'echo',  label: 'Echo (Male)' },
    { value: 'fable', label: 'Fable (Male)' },
    { value: 'onyx',  label: 'Onyx (Male)' },
    { value: 'nova',  label: 'Nova (Female)' },
    { value: 'sage',  label: 'Sage (Female)' },
    { value: 'shimmer', label: 'Shimmer (Female)' },
    { value: 'verse', label: 'Verse (Neutral)' },
];

// ── Default form state ──────────────────────────────────────────────────────

const DEFAULT_FORM: AiTranslatorProfileUpdatePayload = {
    name: '',
    description: '',
    nativeLanguage: 'en',
    voice: 'alloy',
    remoteVoice: 'nova',
    aiProfileId: null,
    enabled: true,
};

// ── Component ───────────────────────────────────────────────────────────────

export default function AiTranslatorProfileFormView() {
    const { id } = useParams<{ id: string }>();
    const isNew = !id || id === 'new';
    const isEditMode = !isNew;

    // UUID is the platform standard for routing — handleEdit always navigates with p.uuid.
    // The :id param will be a UUID (e.g. "b3c4-...") or 'new'.
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const isUuid = !isNew && !!id && UUID_RE.test(id);
    const isValidId = !isNew && !!id && id !== 'undefined' && id !== 'null';

    const navigate = useNavigate();
    const { showSuccess, showError } = useNotifications();
    const { t } = useI18n();
    const translateRef = useRef(t);
    useEffect(() => { translateRef.current = t; }, [t]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [aiProfiles, setAiProfiles] = useState<AiProfile[]>([]);
    const [formData, setFormData] = useState<AiTranslatorProfileUpdatePayload>(DEFAULT_FORM);
    const [originalFormData, setOriginalFormData] = useState<AiTranslatorProfileUpdatePayload>(DEFAULT_FORM);
    // resolvedId: the raw id string to use in API calls (Sqid or numeric string)
    // The backend Sqids plugin will decode it correctly
    const [resolvedId, setResolvedId] = useState<string | null>(isValidId ? id! : null);

    const handleChange = useCallback(<K extends keyof AiTranslatorProfileUpdatePayload>(
        field: K,
        value: AiTranslatorProfileUpdatePayload[K]
    ) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Load AI profiles for the dropdown
    useEffect(() => {
        getAiProfiles({ page: 1, limit: 100, sortBy: 'profile_name', sortOrder: 'asc', type: 'agent_realtime' })
            .then(res => setAiProfiles(res.data || []))
            .catch(err => console.error('Failed to fetch AI profiles', err));
    }, []);

    // Load existing profile for edit mode
    const fetchProfile = useCallback(async () => {
        if (isNew) return;

        if (!isValidId) {
            showError('Invalid Profile ID. Please select a profile from the list.');
            navigate('/admin/ai-integration/translator-profiles');
            return;
        }

        setLoading(true);
        try {
            // The backend accepts UUID or integer in :id — UUID is the routing standard
            const res = await aiTranslatorProfilesApi.getProfile(id!);
            const profileData = res?.data ?? res;

            if (profileData && profileData.name !== undefined) {
                setFormData(profileData);
                setOriginalFormData(profileData);
                // Store the UUID (from response) for update/delete operations
                if (profileData.uuid) setResolvedId(profileData.uuid);
            } else {
                throw new Error('Invalid profile data received from server');
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            showError(translateRef.current('common.errorFetching') || 'Error fetching profile');
        } finally {
            setLoading(false);
        }
    }, [isNew, id, isValidId, navigate, showError]);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    // Save handler
    const handleSave = async () => {
        setSaving(true);
        try {
            const payload: AiTranslatorProfileUpdatePayload = {
                ...formData,
                aiProfileId: formData.aiProfileId && String(formData.aiProfileId) !== 'none'
                    ? Number(formData.aiProfileId)
                    : null,
            };

            if (isEditMode) {
                // Use the UUID from URL (or resolved from response) — backend accepts UUID in :id
                const uuidForUpdate = resolvedId || id;
                if (!uuidForUpdate || uuidForUpdate === 'undefined') throw new Error('Invalid Profile UUID');
                const result = await aiTranslatorProfilesApi.updateProfile(uuidForUpdate, payload);
                showSuccess(translateRef.current('common.savedSuccessfully') || 'Saved successfully');
                setOriginalFormData(payload);
                return result?.data || { id: uuidForUpdate };
            } else {
                const res = await aiTranslatorProfilesApi.createProfile(payload as any);
                showSuccess(translateRef.current('common.createdSuccessfully') || 'Created successfully');
                setOriginalFormData(payload);
                return res?.data;
            }
        } catch (err) {
            console.error('Save error:', err);
            showError(translateRef.current('common.errorSaving') || 'Failed to save');
            return undefined;
        } finally {
            setSaving(false);
        }
    };




    const handleSaveWithAction = async (action: 'save' | 'save-and-close' | 'save-and-new') => {
        const result = await handleSave();
        if (!result) return;
        if (action === 'save' && isNew && result.id) {
            navigate(`/admin/ai-integration/translator-profiles/${result.id}`, { replace: true });
        } else if (action === 'save-and-close') {
            navigate('/admin/ai-integration/translator-profiles');
        } else if (action === 'save-and-new') {
            setFormData(DEFAULT_FORM);
            setOriginalFormData(DEFAULT_FORM);
            navigate('/admin/ai-integration/translator-profiles/new');
        }
    };

    const handleCancel = useCallback(() => {
        setFormData({ ...originalFormData });
    }, [originalFormData]);

    const handleBack = useCallback(() => {
        navigate('/admin/ai-integration/translator-profiles');
    }, [navigate]);

    const handleDeleteClick = useCallback(() => {
        setShowDeleteModal(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        const idToDelete = resolvedId || id;
        if (!idToDelete) return;
        setIsDeleting(true);
        try {
            await aiTranslatorProfilesApi.deleteProfile(idToDelete);
            showSuccess(t('common.deletedSuccessfully') || 'Deleted successfully');
            navigate('/admin/ai-integration/translator-profiles');
        } catch {
            showError(t('common.errorDeleting') || 'Failed to delete');
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    }, [resolvedId, id, navigate, showSuccess, showError, t]);

    // Build AI profile select options
    const aiProfileOptions = [
        { value: 'none', label: '— Default System Model —' },
        ...aiProfiles.map(p => ({
            value: String(p.id),
            label: p.profile_name || (p as any).name || `Profile ${p.id}`,
            subLabel: p.model
        }))
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden p-6 pb-0">

            {/* Header */}
            <ModuleFormHeader
                title={isEditMode
                    ? `${translateRef.current('common.edit') || 'Edit'} — ${formData.name || ''}`
                    : translateRef.current('common.add') || 'New Translator Profile'
                }
                icon={<Languages className="h-5 w-5 text-primary" />}
                onBack={handleBack}
                backButtonText={translateRef.current('common.list') || 'List'}
            />

            {/* Scrollable Form Content */}
            <div className="flex-1 overflow-auto pb-24 mt-8">
                <div className="space-y-4">

                    {/* General Information */}
                    <FormBox
                        title="Profile General Information"
                        description="Set up the translator profile basic details."
                        icon={<Languages className="h-5 w-5" />}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                            {/* Row 1: Name | Description */}
                            <SimpleTooltip content="Name of the translator profile" position="top">
                                <Label required className="text-left">Name</Label>
                            </SimpleTooltip>
                            <input
                                id="profile-name"
                                type="text"
                                required
                                value={formData.name || ''}
                                onChange={e => handleChange('name', e.target.value)}
                                className="px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                                placeholder="e.g. Spanish Translator"
                            />
                            <SimpleTooltip content="Describe what this translator profile does" position="top">
                                <Label className="text-left">Description</Label>
                            </SimpleTooltip>
                            <input
                                id="profile-description"
                                type="text"
                                value={formData.description || ''}
                                onChange={e => handleChange('description', e.target.value)}
                                className="px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                                placeholder="Describe what this translator profile does..."
                            />

                            {/* Row 2: Enabled */}
                            <SimpleTooltip content="Enable or disable this profile" position="top">
                                <Label className="text-left">Enabled</Label>
                            </SimpleTooltip>
                            <Toggle
                                value={formData.enabled ?? true}
                                onChange={(v) => handleChange('enabled', v)}
                                id="enabled"
                            />
                            <div /><div />
                        </div>
                    </FormBox>

                    {/* Translation Settings */}
                    <FormBox
                        title="Translation Settings"
                        description="Configure the language and AI properties for real-time translation."
                        icon={<Languages className="h-5 w-5" />}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                            {/* Row 1: Native Language | AI Profile */}
                            <SimpleTooltip content="Select the primary language of the extension owner" position="top">
                                <Label required className="text-left">Native Language</Label>
                            </SimpleTooltip>
                            <SearchableSelect
                                id="native-language"
                                value={formData.nativeLanguage || 'en'}
                                onChange={(v) => handleChange('nativeLanguage', v)}
                                placeholder="Select language"
                                options={LANGUAGE_OPTIONS}
                            />
                            <SimpleTooltip content="Select an AI Profile (gpt-4o-realtime) to use for simultaneous translation." position="top">
                                <Label className="text-left">AI Profile</Label>
                            </SimpleTooltip>
                            <SearchableSelect
                                id="ai-profile"
                                value={formData.aiProfileId?.toString() || 'none'}
                                onChange={(val) => handleChange('aiProfileId', (!val || val === 'none') ? null : (val as any))}
                                placeholder="Select an AI Profile..."
                                options={aiProfileOptions}
                            />

                            {/* Row 2: Extension Voice | Remote Voice */}
                            <SimpleTooltip content="Select the AI voice used to speak to the extension owner" position="top">
                                <Label className="text-left">Extension Voice</Label>
                            </SimpleTooltip>
                            <SearchableSelect
                                id="ai-voice"
                                value={formData.voice || 'alloy'}
                                onChange={(v) => handleChange('voice', v)}
                                placeholder="Select voice"
                                options={VOICE_OPTIONS}
                            />
                            <SimpleTooltip content="Select the AI voice used to speak to the remote caller" position="top">
                                <Label className="text-left">Remote Voice</Label>
                            </SimpleTooltip>
                            <SearchableSelect
                                id="remote-voice"
                                value={formData.remoteVoice || 'nova'}
                                onChange={(v) => handleChange('remoteVoice', v)}
                                placeholder="Select remote voice"
                                options={VOICE_OPTIONS}
                            />
                        </div>
                    </FormBox>

                </div>
            </div>

            {/* Fixed Action Bar */}
            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                onDelete={isEditMode ? handleDeleteClick : undefined}
                isEditMode={isEditMode}
                isSaving={saving}
                deleteDisabled={isDeleting}
                cancelText={translateRef.current('common.cancel') || 'Cancel'}
                deleteText={translateRef.current('common.delete') || 'Delete'}
                useSaveDropdown={true}
                saveDropdownStorageKey="aiTranslatorProfiles:saveAction"
                saveDisabled={saving || !formData.name?.trim()}
            />

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                title={translateRef.current('common.deleteItem') || 'Delete Profile'}
                message={`${translateRef.current('common.areYouSureDeleteItem') || 'Are you sure you want to delete'} "${(formData as any).name}"?`}
                confirmText={translateRef.current('common.delete') || 'Delete'}
                cancelText={translateRef.current('common.cancel') || 'Cancel'}
                isLoading={isDeleting}
                type="danger"
            />
        </div>
    );
}
